import { useCallback, useEffect, useRef, useState } from 'react';
import {
  deleteDocument as apiDelete,
  isDocumentReadyForAudio,
  listDocuments,
  processDocumentFull,
  uploadDocument,
} from '../api/documentsApi';
import { DocumentItem } from '../types';
import { useAuth } from '../auth/AuthContext';

export interface DocumentsState {
  documents: DocumentItem[];
  loading: boolean;
  error: string | null;
  uploading: boolean;
  processingIds: Set<number>;
  refresh: () => void;
  upload: (file: File) => Promise<void>;
  remove: (id: number) => Promise<void>;
  isReady: (doc: DocumentItem) => boolean;
}

const POLL_INTERVAL_MS = 5000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

export function useDocuments(): DocumentsState {
  const { userId } = useAuth();
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [processingIds, setProcessingIds] = useState<Set<number>>(new Set());
  const pollTimers = useRef<Map<number, ReturnType<typeof setInterval>>>(new Map());

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const docs = await listDocuments(userId);
      setDocuments(docs);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  // Poll a single document until it becomes RAG-ready or times out.
  const startPolling = useCallback((id: number) => {
    if (pollTimers.current.has(id)) return;
    setProcessingIds((prev) => new Set(prev).add(id));
    const startedAt = Date.now();
    const timer = setInterval(async () => {
      try {
        const docs = await listDocuments(userId!);
        setDocuments(docs);
        const doc = docs.find((d) => d.documentId === id);
        const ready = doc ? isDocumentReadyForAudio(doc) : false;
        if (ready || Date.now() - startedAt > POLL_TIMEOUT_MS) {
          clearInterval(timer);
          pollTimers.current.delete(id);
          setProcessingIds((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
        }
      } catch {
        /* keep polling */
      }
    }, POLL_INTERVAL_MS);
    pollTimers.current.set(id, timer);
  }, [userId]);

  useEffect(() => {
    const timers = pollTimers.current;
    return () => {
      timers.forEach((t) => clearInterval(t));
      timers.clear();
    };
  }, []);

  const upload = useCallback(
    async (file: File) => {
      if (!userId) throw new Error('Not signed in');
      setUploading(true);
      setError(null);
      try {
        const { documentId } = await uploadDocument({ file, userId });
        await load();
        // Fire RAG processing, then poll until search-ready.
        processDocumentFull(documentId).catch(() => undefined);
        startPolling(documentId);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Upload failed');
        throw e;
      } finally {
        setUploading(false);
      }
    },
    [userId, load, startPolling],
  );

  const remove = useCallback(async (id: number) => {
    await apiDelete(id);
    setDocuments((prev) => prev.filter((d) => d.documentId !== id));
  }, []);

  return {
    documents,
    loading,
    error,
    uploading,
    processingIds,
    refresh: load,
    upload,
    remove,
    isReady: isDocumentReadyForAudio,
  };
}
